import React, { useState, useMemo } from 'react';
import {
  Trophy,
  Medal,
  Award,
  TrendingUp,
  TrendingDown,
  User,
  Calendar,
  AlertTriangle,
  GraduationCap,
  Eye,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';
import DriverRiskBadge, { RiskScoreGauge, getRiskCategory, RISK_CONFIG } from '../badges/DriverRiskBadge.jsx';

/**
 * DriverLeagueTable - Ranking table for driver behavior scores
 * Similar to Quartix league tables
 */
function DriverLeagueTable({
  drivers = [],
  riskProfiles = [],
  onViewDriver,
  className = ''
}) {
  const { t, fmt } = useFormat();
  const [sortField, setSortField] = useState('rank');
  const [sortDirection, setSortDirection] = useState('asc');

  // Merge drivers with their risk profiles
  const driversWithRisk = useMemo(() => {
    return drivers.map(driver => {
      const profile = riskProfiles.find(p => p.driver_id === driver.id) || {
        current_risk_score: 100,
        risk_category: 'average',
        training_bonus: 0,
        claims_malus: 0,
        experience_bonus: 0,
        claims_last_12_months: 0,
        training_courses_completed: 0,
        last_claim_date: null,
      };
      return { ...driver, riskProfile: profile };
    });
  }, [drivers, riskProfiles]);

  // Sort and rank drivers
  const rankedDrivers = useMemo(() => {
    const sorted = [...driversWithRisk].sort((a, b) => {
      // Primary sort by risk score (lower is better)
      const scoreA = a.riskProfile.current_risk_score || 100;
      const scoreB = b.riskProfile.current_risk_score || 100;
      return scoreA - scoreB;
    });

    // Assign ranks
    return sorted.map((driver, index) => ({
      ...driver,
      rank: index + 1,
    }));
  }, [driversWithRisk]);

  // Apply current sorting
  const sortedDrivers = useMemo(() => {
    const sorted = [...rankedDrivers];

    sorted.sort((a, b) => {
      let valueA, valueB;

      switch (sortField) {
        case 'rank':
          valueA = a.rank;
          valueB = b.rank;
          break;
        case 'name':
          valueA = `${a.last_name} ${a.first_name}`.toLowerCase();
          valueB = `${b.last_name} ${b.first_name}`.toLowerCase();
          break;
        case 'score':
          valueA = a.riskProfile.current_risk_score || 100;
          valueB = b.riskProfile.current_risk_score || 100;
          break;
        case 'claims':
          valueA = a.riskProfile.claims_last_12_months || 0;
          valueB = b.riskProfile.claims_last_12_months || 0;
          break;
        case 'training':
          valueA = a.riskProfile.training_courses_completed || 0;
          valueB = b.riskProfile.training_courses_completed || 0;
          break;
        default:
          return 0;
      }

      if (typeof valueA === 'string') {
        return sortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
    });

    return sorted;
  }, [rankedDrivers, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  // Render rank badge with special icons for top 3
  const RankBadge = ({ rank }) => {
    if (rank === 1) {
      return (
        <div className="flex items-center gap-1">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <span className="font-bold text-yellow-600">1</span>
        </div>
      );
    }
    if (rank === 2) {
      return (
        <div className="flex items-center gap-1">
          <Medal className="w-5 h-5 text-gray-400" />
          <span className="font-bold text-gray-600">2</span>
        </div>
      );
    }
    if (rank === 3) {
      return (
        <div className="flex items-center gap-1">
          <Award className="w-5 h-5 text-amber-600" />
          <span className="font-bold text-amber-700">3</span>
        </div>
      );
    }
    return <span className="font-medium text-gray-600">{rank}</span>;
  };

  // Calculate summary stats
  const stats = useMemo(() => {
    const scores = rankedDrivers.map(d => d.riskProfile.current_risk_score || 100);
    const avgScore = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 100;
    const excellent = rankedDrivers.filter(d => getRiskCategory(d.riskProfile.current_risk_score) === 'excellent').length;
    const high = rankedDrivers.filter(d => getRiskCategory(d.riskProfile.current_risk_score) === 'high').length;

    return {
      total: rankedDrivers.length,
      avgScore: Math.round(avgScore),
      excellent,
      high,
    };
  }, [rankedDrivers]);

  if (drivers.length === 0) {
    return (
      <div className={`bg-white rounded-lg shadow p-8 text-center ${className}`}>
        <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500">{t('drivers.league.empty')}</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {/* Header with Stats */}
      <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-800">{t('drivers.league.title')}</h3>
          </div>
          <div className="text-sm text-gray-500">
            {t('drivers.list.driversCount', { count: stats.total })}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-2xl font-bold text-gray-800">{stats.avgScore}</div>
            <div className="text-xs text-gray-500">{t('drivers.league.avgScore')}</div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-2xl font-bold text-emerald-600">{stats.excellent}</div>
            <div className="text-xs text-gray-500">{t('drivers.league.excellent')}</div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-2xl font-bold text-red-600">{stats.high}</div>
            <div className="text-xs text-gray-500">{t('drivers.league.highRisk')}</div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-2xl font-bold text-blue-600">
              {rankedDrivers.filter(d => (d.riskProfile.training_courses_completed || 0) > 0).length}
            </div>
            <div className="text-xs text-gray-500">{t('drivers.league.trained')}</div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('rank')}
              >
                <div className="flex items-center gap-1">
                  # <SortIcon field="rank" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  {t('drivers.columns.driver')} <SortIcon field="name" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('score')}
              >
                <div className="flex items-center gap-1">
                  {t('drivers.league.score')} <SortIcon field="score" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('drivers.league.category')}
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('claims')}
              >
                <div className="flex items-center gap-1">
                  {t('drivers.league.claims')} <SortIcon field="claims" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('training')}
              >
                <div className="flex items-center gap-1">
                  {t('drivers.league.training')} <SortIcon field="training" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('drivers.league.lastClaim')}
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('drivers.columns.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedDrivers.map((driver) => {
              const score = driver.riskProfile.current_risk_score || 100;
              const category = getRiskCategory(score);
              const config = RISK_CONFIG[category] || RISK_CONFIG.average;

              return (
                <tr
                  key={driver.id}
                  className={`hover:bg-gray-50 ${driver.rank <= 3 ? 'bg-gradient-to-r from-yellow-50/30 to-transparent' : ''}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <RankBadge rank={driver.rank} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.bg}`}>
                        <User className={`w-4 h-4 ${config.text}`} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {driver.first_name} {driver.last_name}
                        </div>
                        <div className="text-xs text-gray-500">{driver.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="w-32">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-lg font-bold ${config.text}`}>
                          {Math.round(score)}
                        </span>
                        {driver.riskProfile.training_bonus < 0 && (
                          <TrendingDown className="w-4 h-4 text-green-500" title={t('drivers.risk.trainingBonus')} />
                        )}
                        {driver.riskProfile.claims_malus > 0 && (
                          <TrendingUp className="w-4 h-4 text-red-500" title={t('drivers.risk.claimsMalus')} />
                        )}
                      </div>
                      <RiskScoreGauge score={score} size="sm" />
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <DriverRiskBadge
                      riskCategory={category}
                      riskScore={score}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {(driver.riskProfile.claims_last_12_months || 0) > 0 ? (
                        <span className="text-red-600 font-medium flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          {driver.riskProfile.claims_last_12_months}
                        </span>
                      ) : (
                        <span className="text-green-600">0</span>
                      )}
                      <span className="text-xs text-gray-400">(12m)</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <GraduationCap className="w-4 h-4 text-blue-500" />
                      <span className="font-medium">
                        {driver.riskProfile.training_courses_completed || 0}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {driver.riskProfile.last_claim_date ? (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {fmt.date(driver.riskProfile.last_claim_date)}
                      </div>
                    ) : (
                      <span className="text-green-600">{t('drivers.league.none')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <button
                      onClick={() => onViewDriver?.(driver)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t('drivers.league.viewProfile')}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DriverLeagueTable;
